// src/components/admin/PreguntasEditor.js
//
// Editor de preguntas y opciones de un cuestionario / examen.
// Se usa dentro del Dialog de "Editar contenido" cuando el tipo es
// Quiz o Examen.
//
// Flujo:
//   1. Al montarse: carga las preguntas del contenido desde el backend
//      (evaluacionService.getPreguntas). Cada pregunta trae su lista
//      de opciones con cual es la correcta.
//   2. El admin puede:
//      - Agregar una pregunta nueva (abre sub-dialog en modo creacion)
//      - Editar una pregunta existente (sub-dialog con datos cargados)
//      - Eliminar una pregunta (confirmacion + soft delete en backend)
//   3. En el sub-dialog de pregunta:
//      - Edita enunciado, explicacion, puntos
//      - Lista editable de opciones: una por fila, con el texto, un
//        radio para marcar cual es la correcta (solo una), boton X
//        para quitar y boton + al final para agregar
//      - Al guardar: POST (crear) o PUT (actualizar) con el payload
//        completo. El backend reconcilia opciones.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Typography, Paper, IconButton, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Radio, Stack, Chip, CircularProgress, Divider,
} from '@mui/material';
import { Add, Edit, Delete, Check } from '@mui/icons-material';
import { evaluacionService } from '../../services/api';

const OPCION_VACIA = { id: null, text: '', isCorrect: false, order: 1 };
const PREGUNTA_VACIA = {
  statement: '',
  explanation: '',
  points: 1,
  order: 1,
  options: [{ ...OPCION_VACIA, order: 1 }, { ...OPCION_VACIA, order: 2 }, { ...OPCION_VACIA, order: 3 }, { ...OPCION_VACIA, order: 4 }],
};

export default function PreguntasEditor({ contenidoId, kind = 'Cuestionario' }) {
  const [preguntas, setPreguntas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState('');

  // Sub-dialog de pregunta
  const [editing, setEditing] = useState(null);       // id de pregunta en edicion ('new' para crear)
  const [form, setForm] = useState(PREGUNTA_VACIA);
  const [guardando, setGuardando] = useState(false);
  const [errorGuardar, setErrorGuardar] = useState('');

  const cargar = useCallback(async () => {
    if (!contenidoId) return;
    setCargando(true);
    setErrorCarga('');
    try {
      const list = await evaluacionService.getPreguntas(contenidoId);
      setPreguntas(list || []);
    } catch (e) {
      setErrorCarga(e.message || 'Error cargando preguntas.');
    } finally {
      setCargando(false);
    }
  }, [contenidoId]);

  useEffect(() => { cargar(); }, [cargar]);

  const nuevaPregunta = () => {
    setEditing('new');
    setForm({
      ...PREGUNTA_VACIA,
      order: (preguntas[preguntas.length - 1]?.order || preguntas.length) + 1,
    });
    setErrorGuardar('');
  };

  const editarPregunta = (p) => {
    setEditing(p.id);
    setForm({
      statement: p.statement || '',
      explanation: p.explanation || '',
      points: p.points ?? 1,
      order: p.order ?? 1,
      options: (p.options || []).map((o) => ({
        id: o.id, text: o.text, isCorrect: !!o.isCorrect, order: o.order,
      })),
    });
    setErrorGuardar('');
  };

  const eliminarPregunta = async (p) => {
    if (!window.confirm(`¿Eliminar la pregunta "${p.statement.slice(0, 60)}..."?`)) return;
    try {
      await evaluacionService.deletePregunta(p.id);
      await cargar();
    } catch (e) {
      alert(e.message || 'No se pudo eliminar.');
    }
  };

  const addOpcion = () => {
    setForm((f) => ({
      ...f,
      options: [...f.options, { ...OPCION_VACIA, order: f.options.length + 1 }],
    }));
  };

  const removeOpcion = (idx) => {
    setForm((f) => ({
      ...f,
      options: f.options.filter((_, i) => i !== idx).map((o, i) => ({ ...o, order: i + 1 })),
    }));
  };

  const updateOpcion = (idx, patch) => {
    setForm((f) => ({
      ...f,
      options: f.options.map((o, i) => (i === idx ? { ...o, ...patch } : o)),
    }));
  };

  const marcarCorrecta = (idx) => {
    setForm((f) => ({
      ...f,
      options: f.options.map((o, i) => ({ ...o, isCorrect: i === idx })),
    }));
  };

  const validar = () => {
    if (!form.statement.trim()) return 'El enunciado no puede estar vacío.';
    if (form.options.length < 2) return 'Debe haber al menos 2 opciones.';
    if (form.options.some((o) => !o.text.trim())) return 'Todas las opciones deben tener texto.';
    if (!form.options.some((o) => o.isCorrect)) return 'Marca una opción como correcta.';
    const correctas = form.options.filter((o) => o.isCorrect).length;
    if (correctas > 1) return 'Solo una opción puede ser la correcta.';
    return null;
  };

  const guardar = async () => {
    const err = validar();
    if (err) { setErrorGuardar(err); return; }

    setGuardando(true);
    setErrorGuardar('');
    try {
      if (editing === 'new') {
        await evaluacionService.createPregunta(contenidoId, form);
      } else {
        await evaluacionService.updatePregunta(editing, form);
      }
      setEditing(null);
      await cargar();
    } catch (e) {
      setErrorGuardar(e.message || 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  };

  // ────────────────────────────────────────────────────────────────
  if (cargando) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Box>
      <Divider sx={{ my: 2 }}>
        <Chip label={`Preguntas del ${kind.toLowerCase()} (${preguntas.length})`} />
      </Divider>

      {errorCarga && <Alert severity="error" sx={{ mb: 2 }}>{errorCarga}</Alert>}

      {preguntas.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Este {kind.toLowerCase()} todavía no tiene preguntas. Agrega la primera abajo.
        </Alert>
      )}

      <Stack spacing={1}>
        {preguntas.map((p, i) => (
          <Paper key={p.id} variant="outlined" sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {i + 1}. {p.statement}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {p.options?.length || 0} opciones • {p.points} {p.points === 1 ? 'pto' : 'pts'}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {(p.options || []).map((o) => (
                    <Typography key={o.id} variant="caption" sx={{ display: 'block', pl: 1, color: o.isCorrect ? 'success.main' : 'text.secondary' }}>
                      {o.isCorrect ? '✓ ' : '• '} {o.text}
                    </Typography>
                  ))}
                </Box>
              </Box>
              <Box>
                <IconButton size="small" title="Editar" onClick={() => editarPregunta(p)}>
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" title="Eliminar" onClick={() => eliminarPregunta(p)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        ))}
      </Stack>

      <Button sx={{ mt: 2 }} startIcon={<Add />} onClick={nuevaPregunta} variant="outlined" size="small">
        Agregar pregunta
      </Button>

      {/* ────────── Sub-dialog crear / editar pregunta ────────── */}
      <Dialog
        open={!!editing}
        onClose={() => !guardando && setEditing(null)}
        maxWidth="md" fullWidth
      >
        <DialogTitle>{editing === 'new' ? 'Nueva pregunta' : 'Editar pregunta'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {errorGuardar && <Alert severity="error" onClose={() => setErrorGuardar('')}>{errorGuardar}</Alert>}

            <TextField
              label="Enunciado"
              value={form.statement}
              onChange={(e) => setForm({ ...form, statement: e.target.value })}
              fullWidth multiline minRows={2} autoFocus
            />

            <TextField
              label="Explicación (se muestra al estudiante tras responder)"
              value={form.explanation}
              onChange={(e) => setForm({ ...form, explanation: e.target.value })}
              fullWidth multiline minRows={2}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Puntos"
                type="number"
                value={form.points}
                onChange={(e) => setForm({ ...form, points: Number(e.target.value) || 0 })}
                sx={{ width: 120 }}
              />
              <TextField
                label="Orden"
                type="number"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 1 })}
                sx={{ width: 120 }}
              />
            </Box>

            <Divider>Opciones</Divider>
            <Typography variant="caption" color="text.secondary">
              Marca con el radio cuál es la opción correcta. Solo una puede serlo.
            </Typography>

            <Stack spacing={1}>
              {form.options.map((o, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Radio
                    checked={!!o.isCorrect}
                    onChange={() => marcarCorrecta(idx)}
                    icon={<Check sx={{ color: 'text.disabled' }} />}
                    checkedIcon={<Check sx={{ color: 'success.main' }} />}
                    size="small"
                  />
                  <TextField
                    value={o.text}
                    onChange={(e) => updateOpcion(idx, { text: e.target.value })}
                    placeholder={`Opción ${idx + 1}`}
                    fullWidth size="small"
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeOpcion(idx)}
                    disabled={form.options.length <= 2}
                    title={form.options.length <= 2 ? 'Debe haber al menos 2 opciones' : 'Quitar'}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Stack>

            <Button startIcon={<Add />} onClick={addOpcion} size="small" sx={{ alignSelf: 'flex-start' }}>
              Agregar opción
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={guardando}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando…' : (editing === 'new' ? 'Crear pregunta' : 'Guardar cambios')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
